import React, { useCallback, useState } from "react";
import {
  View,
  FlatList,
  ActivityIndicator,
  Alert,
  Pressable,
  StyleSheet,
} from "react-native";
import { useFocusEffect } from "@react-navigation/native";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import Text from "@components/ds/DsText";
import { t } from "@constants/localization";
import {
  getReceivingTpDetail,
  scanReceivingShipment,
  markRackFull,
  completeReceivingTp,
} from "@services/api/receiving";
import NativeScannerSheet from "@components/ds/NativeScannerSheet";
import ScanInput from "../components/ScanInput";
import ProgressBar, { toPercent } from "@components/ds/ProgressBar";
import Banner from "@components/ds/Banner";
import BottomButton from "@components/ds/BottomButton";
import ShipmentRosterRow from "../components/ShipmentRosterRow";
import TpSummaryCard from "../components/TpSummaryCard";
import { showReceivingError } from "../showError";
import {
  getActiveRack,
  getCardDateText,
  getNextPendingId,
  getScanState,
  getTpTitleParts,
  groupShipmentsByRack,
} from "../helpers";
import { tokens, sharedStyles } from "@components/ds/tokens";
import Pill from "@components/ds/Pill";

const PATH = "Receiving/scanShipments";
const TP_PATH = "Receiving/tpList";

// Registered as `ReceivingScanShipments`. Template: MyTasks'
// PickupConfirmationModal skeleton (header counter + FlatList + sticky
// footer) adapted to a full screen, plus the same scanner sheet as
// ReceivingScanRack. Same visual language as that screen (spec p2 frame
// 4, p3 frames 5-6): TP summary card, a green "Rack B-04 · रैक असाइन हो
// गया" chip, scanned counter + progress bar, one scan input, roster rows
// with status pills, a text-link "+ दूसरा रैक जोड़ें", banners, full-width
// CTA.

const ReceivingScanShipments = ({ navigation, route }) => {
  const { cx_id, date, cx_name, area, rack: paramRack } = route.params || {};

  const [roster, setRoster] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [scannerVisible, setScannerVisible] = useState(false);

  const fetchRoster = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getReceivingTpDetail({ cx_id, date });
      setRoster(data?.data || null);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [cx_id, date]);

  useFocusEffect(
    useCallback(() => {
      fetchRoster();
    }, [fetchRoster]),
  );

  const shipments = roster?.shipments || [];
  const racks = roster?.racks || [];
  const scanned = roster?.scanned ?? 0;
  const total = roster?.total ?? 0;

  // The rack picked on ReceivingScanRack may not have shown up in `racks[]`
  // yet (nothing has been scanned onto it) — fold it into the rack list
  // until the first scan lands and the roster catches up. Both `activeRack`
  // and `scanState` are derived from this same list so they can't disagree
  // (a bug caught in review: computing scanState straight off the server
  // `racks[]` would show "needs a new rack" for the first few seconds
  // after choosing one, since the roster hadn't caught up yet).
  const effectiveRacks =
    !getActiveRack(racks) && paramRack
      ? racks.concat([{ ...paramRack, is_full: false }])
      : racks;
  const activeRack = getActiveRack(effectiveRacks);
  const scanState = getScanState({ scanned, total, racks: effectiveRacks });

  const submitScan = async (shipmentId, scanKind) => {
    if (!activeRack || submitting) return;
    try {
      setSubmitting(true);
      const response = await scanReceivingShipment({
        cx_id,
        date,
        rack_id: activeRack.rack_id,
        shipment_id: shipmentId,
        scan_kind: scanKind,
      });
      if (response?.data?.data) {
        setRoster(response.data.data);
      } else {
        // Same shared-interceptor gap as ReceivingScanRack.handleResolve —
        // a 404 LABEL_NOT_FOUND resolves here as an empty `response`
        // instead of throwing. See errors.js header comment.
        showReceivingError();
      }
    } catch (error) {
      console.error(error);
      const code = showReceivingError(error);
      // The rack holds another customer's boxes: send the executive straight
      // back to the rack picker. Not via "+ Add another rack", whose "is this
      // rack full?" prompt would mark someone else's rack full.
      // RACK_OTHER_EDD: this customer's boxes for another date (advance
      // racked) — same: pick another rack.
      if (code === "RACK_OTHER_CX" || code === "RACK_OTHER_EDD") goToAddRack();
    } finally {
      setSubmitting(false);
    }
  };

  const onManualSubmit = (value) => submitScan(value, "MANUAL");
  const onScanDecode = (value) => {
    setScannerVisible(false);
    submitScan(value, "SCANNED");
  };

  const goToAddRack = () => {
    navigation.replace("ReceivingScanRack", {
      cx_id,
      date,
      cx_name,
      area,
      shipment_count: total,
    });
  };

  const onPressAddAnotherRack = () => {
    if (!activeRack) {
      goToAddRack();
      return;
    }
    Alert.alert(
      t(`${PATH}/markFullConfirmTitle`, { rack_no: activeRack.rack_no }),
      undefined,
      [
        {
          text: t(`${PATH}/markFullConfirmNo`),
          style: "cancel",
          onPress: goToAddRack,
        },
        {
          text: t(`${PATH}/markFullConfirmYes`),
          onPress: async () => {
            try {
              await markRackFull({
                rack_id: activeRack.rack_id,
                is_full: true,
              });
            } catch (error) {
              console.error(error);
            }
            goToAddRack();
          },
        },
      ],
    );
  };

  const onPressComplete = async () => {
    try {
      setCompleting(true);
      await completeReceivingTp({ cx_id, date });
      // Back to the list this card came from (a racking-hub bucket list, or
      // the legacy list); it reloads on focus.
      navigation.goBack();
    } catch (error) {
      console.error(error);
      showReceivingError(error);
    } finally {
      setCompleting(false);
    }
  };

  const nextPendingId = getNextPendingId(shipments);
  const groups = groupShipmentsByRack(shipments, effectiveRacks);
  const lastRack = effectiveRacks[effectiveRacks.length - 1];

  // Prefer the roster's own cx_name (freshest) over the nav param.
  const { hasName, name } = getTpTitleParts({
    cx_name: roster?.cx_name ?? cx_name,
  });
  const tpMeta = hasName
    ? t(`${TP_PATH}/rowMetaWithId`, {
        cx_id,
        count: String(total),
        area: (roster?.area ?? area) || "",
      })
    : t(`${TP_PATH}/rowMeta`, {
        count: String(total),
        area: (roster?.area ?? area) || "",
      });
  const dateText = getCardDateText(roster?.date ?? date, t);
  const metaText = dateText ? `${tpMeta} · ${dateText}` : tpMeta;

  const rows = groups
    ? groups.flatMap((group) => [
        { _type: "GROUP_HEADER", group },
        ...group.shipments.map((shipment) => ({ _type: "ROW", shipment })),
      ])
    : shipments.map((shipment) => ({ _type: "ROW", shipment }));

  const renderItem = ({ item }) => {
    if (item._type === "GROUP_HEADER") {
      const { group } = item;
      return (
        <View
          style={styles.groupHeader}
          testID={`receiving-rack-group-${group.rack.rack_no}`}
        >
          <Text variant="caption_2" style={styles.groupHeaderText}>
            {group.isActive
              ? t(`${PATH}/rackGroupActive`, { rack_no: group.rack.rack_no })
              : t(`${PATH}/rackGroupFull`, {
                  rack_no: group.rack.rack_no,
                  count: String(group.shipments.length),
                })}
          </Text>
        </View>
      );
    }
    return (
      <ShipmentRosterRow
        shipment={item.shipment}
        nextPendingId={nextPendingId}
      />
    );
  };

  if (loading && !roster) {
    return (
      <View style={sharedStyles.screen}>
        <HeaderWithBackAndTitle
          headertitle={t(`${PATH}/headerTitle`)}
          headerColor={tokens.surface}
          navigation={navigation}
          route={route}
        />
        <ActivityIndicator
          size="large"
          color={tokens.primary}
          style={sharedStyles.centered}
        />
      </View>
    );
  }

  return (
    <View style={sharedStyles.screen}>
      <HeaderWithBackAndTitle
        headertitle={t(`${PATH}/headerTitle`)}
        headerColor={tokens.surface}
        navigation={navigation}
        route={route}
      />
      <TpSummaryCard
        testID="receiving-scan-shipments-tp-card"
        titleText={hasName ? name : t(`${TP_PATH}/tpLabel`, { cx_id })}
        metaText={metaText}
      />

      {activeRack ? (
        <View testID="receiving-active-rack-chip" style={styles.rackChipRow}>
          <Pill
            label={`Rack ${activeRack.rack_no} · ${t(`${PATH}/rackAssigned`)}`}
            tone="racked"
          />
        </View>
      ) : null}

      <View style={styles.progressBlock}>
        <Text
          variant="caption_2"
          style={styles.scannedCount}
          testID="receiving-scanned-count"
        >
          {t(`${PATH}/scannedCount`, {
            scanned: String(scanned),
            total: String(total),
          })}
        </Text>
        <ProgressBar
          percent={toPercent(scanned, total)}
          testID="receiving-progress-bar"
        />
      </View>

      {scanState === "COMPLETE" ? (
        <Banner tone="success" testID="receiving-complete-banner">
          {t(`${PATH}/completeBanner`, { count: String(total) })}
        </Banner>
      ) : scanState === "NEEDS_NEW_RACK" ? (
        <Banner tone="warning" testID="receiving-overflow-banner">
          {lastRack
            ? t(`${PATH}/overflowBanner`, { rack_no: lastRack.rack_no })
            : t(`${PATH}/needsNewRackBanner`)}
        </Banner>
      ) : null}

      {scanState === "SCANNING" ? (
        <ScanInput
          placeholder={t(`${PATH}/inputPlaceholder`)}
          onManualSubmit={onManualSubmit}
          onScanPress={() => setScannerVisible(true)}
          disabled={submitting}
          testIDPrefix="receiving-scan-shipment"
        />
      ) : null}

      <Text variant="caption_2" style={styles.rosterTitle}>
        {t(`${PATH}/rosterTitle`)}
      </Text>

      <FlatList
        testID="receiving-shipment-roster"
        style={sharedStyles.flex1}
        data={rows}
        keyExtractor={(item, index) =>
          item._type === "GROUP_HEADER"
            ? `group-${item.group.rack.rack_id}`
            : item.shipment.shipment_id || String(index)
        }
        renderItem={renderItem}
        refreshing={loading}
        onRefresh={fetchRoster}
      />

      <Pressable
        onPress={onPressAddAnotherRack}
        testID="receiving-add-another-rack"
        style={styles.addRackRow}
      >
        <Text variant="body_text_1" style={styles.addRackText}>
          {t(`${PATH}/addAnotherRack`)}
        </Text>
      </Pressable>

      <BottomButton
        title={t(`${PATH}/cta`)}
        disabled={scanState !== "COMPLETE"}
        loading={completing}
        onPress={onPressComplete}
        testID="receiving-complete-cta"
      />

      <NativeScannerSheet
        flow="receiving_shipment"
        visible={scannerVisible}
        onClose={() => setScannerVisible(false)}
        onDecode={onScanDecode}
      />
    </View>
  );
};

export default ReceivingScanShipments;

const styles = StyleSheet.create({
  rackChipRow: {
    flexDirection: "row",
    paddingHorizontal: tokens.spacing.large,
    marginBottom: tokens.spacing.small,
  },
  progressBlock: {
    paddingHorizontal: tokens.spacing.large,
    marginBottom: tokens.spacing.small,
  },
  scannedCount: {
    marginBottom: tokens.spacing.tiny,
    color: tokens.textSecondary,
  },
  // variant="caption_2" (Regular) — Bold/SemiBold Devanagari silently
  // drops trailing text on this build (see TabList.js commit notes).
  rosterTitle: {
    paddingHorizontal: tokens.spacing.large,
    paddingTop: tokens.spacing.small,
    paddingBottom: tokens.spacing.tiny,
    color: tokens.textTertiary,
  },
  groupHeader: {
    paddingHorizontal: tokens.spacing.large,
    paddingVertical: tokens.spacing.small,
    backgroundColor: tokens.bg,
  },
  groupHeaderText: { color: tokens.textSecondary },
  addRackRow: {
    paddingHorizontal: tokens.spacing.large,
    paddingVertical: tokens.spacing.medium,
    alignItems: "center",
  },
  // variant="body_text_1" (Regular) — see the rosterTitle note above.
  addRackText: { color: tokens.primaryDark },
});
